import { generateEventId } from '@/utils/eventTracking'

/**
 * Analytics utility supporting Google Analytics, TikTok Pixel, and Facebook Pixel
 *
 * Features:
 * - Automatic SDK initialization checking for TikTok and Facebook Pixel
 * - Event queuing with timeout for SDK readiness
 * - User identification support for all platforms
 * - Backward compatibility with Google Analytics
 *
 * Usage Examples:
 *
 * // ViewContent event for all platforms
 * trackEvent('ViewContent', {
 *   contents: [{ content_id: '123', content_name: 'Product Name' }],
 *   value: 99.99,
 *   currency: 'USD'
 * });
 *
 * // AddToCart with user identification
 * trackEvent('AddToCart', {
 *   contents: [{ content_id: '123', content_name: 'Product Name' }],
 *   value: 99.99,
 *   currency: 'USD',
 *   userData: { email: 'hashed_email@example.com' }
 * });
 *
 * // Search event
 * trackEvent('Search', {
 *   contents: [{ content_id: '123', content_name: 'Search Result' }],
 *   value: 0,
 *   currency: 'USD',
 *   search_string: 'red shoes'
 * });
 *
 * // Backward compatibility with Google Analytics
 * trackEvent('ecommerce', {
 *   category: 'ecommerce',
 *   action: 'purchase',
 *   label: 'Product Purchase',
 *   value: 99.99
 * });
 */

// Check if TikTok Pixel SDK is ready
function isTikTokReady() {
  return process.client &&
         window.ttq &&
         typeof window.ttq.track === 'function' &&
         typeof window.ttq.identify === 'function';
}

// Check if Facebook Pixel SDK is ready
function isFacebookReady() {
  return process.client &&
         window.fbq &&
         typeof window.fbq === 'function';
}

// Wait for TikTok SDK to be ready with timeout
function waitForTikTokReady(timeout = 5000) {
  return new Promise((resolve) => {
    if (isTikTokReady()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkReady = () => {
      if (isTikTokReady()) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        console.warn('TikTok SDK initialization timeout');
        resolve(false);
        return;
      }

      setTimeout(checkReady, 100);
    };

    checkReady();
  });
}

// Wait for Facebook Pixel SDK to be ready with timeout
function waitForFacebookReady(timeout = 5000) {
  return new Promise((resolve) => {
    if (isFacebookReady()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkReady = () => {
      if (isFacebookReady()) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        console.warn('Facebook Pixel SDK initialization timeout');
        resolve(false);
        return;
      }

      setTimeout(checkReady, 100);
    };

    checkReady();
  });
}

// TikTok Pixel event tracking
async function trackTikTokEvent(eventName, parameters = {}) {
  try {
    // Wait for TikTok SDK to be ready
    const isReady = await waitForTikTokReady();

    if (!isReady) {
      console.warn('TikTok SDK not ready, skipping event:', eventName);
      return;
    }

    window.ttq.track(eventName, parameters);
  } catch (e) {
    console.warn('TikTok tracking error:', e);
  }
}

// Facebook Pixel event tracking
async function trackFacebookEvent(eventName, parameters = {}, options = {}) {
  try {
    // Wait for Facebook Pixel SDK to be ready
    const isReady = await waitForFacebookReady();

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping event:', eventName);
      return;
    }

    if (options.eventId) {
      window.fbq('track', eventName, parameters, {
        eventID: options.eventId
      });
      return;
    }

    window.fbq('track', eventName, parameters);
  } catch (e) {
    console.warn('Facebook tracking error:', e);
  }
}

// TikTok user identification
async function identifyTikTokUser(userData = {}) {
  try {
    // Wait for TikTok SDK to be ready
    const isReady = await waitForTikTokReady();

    if (!isReady) {
      console.warn('TikTok SDK not ready, skipping user identification');
      return;
    }

    const identifyData = {};

    if (userData.email) {
      identifyData.email = userData.email; // Should be SHA-256 hashed
    }
    if (userData.phone_number) {
      identifyData.phone_number = userData.phone_number; // Should be SHA-256 hashed
    }
    if (userData.external_id) {
      identifyData.external_id = userData.external_id; // Should be SHA-256 hashed
    }

    if (Object.keys(identifyData).length > 0) {
      window.ttq.identify(identifyData);
    }
  } catch (e) {
    console.warn('TikTok identify error:', e);
  }
}

// Facebook user identification
async function identifyFacebookUser(userData = {}) {
  try {
    // Wait for Facebook Pixel SDK to be ready
    const isReady = await waitForFacebookReady();

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping user identification');
      return;
    }

    const identifyData = {};

    if (userData.email) {
      identifyData.em = userData.email; // Should be SHA-256 hashed
    }
    if (userData.phone_number) {
      identifyData.ph = userData.phone_number; // Should be SHA-256 hashed
    }
    if (userData.first_name) {
      identifyData.fn = userData.first_name; // Should be SHA-256 hashed
    }
    if (userData.last_name) {
      identifyData.ln = userData.last_name; // Should be SHA-256 hashed
    }
    if (userData.external_id) {
      identifyData.external_id = userData.external_id; // Should be SHA-256 hashed
    }

    if (Object.keys(identifyData).length > 0) {
      // Facebook Pixel uses userData in event parameters or setUserData
      window.fbq('set', 'userData', identifyData);
    }
  } catch (e) {
    console.warn('Facebook identify error:', e);
  }
}

// Google Analytics event tracking (backward compatibility)
function trackGoogleAnalytics(category, action, label = "", value = 0) {
  try {
    if (process.client && window.gtag) {
      window.gtag("event", action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  } catch (e) {}
}

/**
 * Clean and normalize value to ensure it's a valid number
 * TikTok requires value to be a number (integer or decimal) without currency symbols, letters, special characters, or commas
 * @param {any} value - The value to clean (can be string, number, etc.)
 * @returns {number} - A valid number (defaults to 0 if invalid)
 */
function normalizeValue(value) {
  // If value is already a valid number, return it
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }

  // If value is undefined, null, or empty, return 0
  if (value === undefined || value === null || value === '') {
    return 0;
  }

  // Convert to string and remove all non-numeric characters except decimal point
  // This removes currency symbols, commas, letters, and other special characters
  let cleanedValue = String(value)
    .replace(/,/g, '') // Remove commas (thousand separators) first
    .replace(/[^\d.]/g, '') // Remove everything except digits and decimal point
    .replace(/\.{2,}/g, '.') // Replace multiple decimal points with single decimal point
    .replace(/^\./, '0.') // Add leading zero if starts with decimal point
    .replace(/\.$/, '') // Remove trailing decimal point
    || '0'; // Default to '0' if empty after cleaning

  // Convert to number
  const numValue = parseFloat(cleanedValue);

  // Return valid number or 0 as fallback
  return isNaN(numValue) || !isFinite(numValue) ? 0 : numValue;
}

/**
 * Enhanced trackEvent function supporting Google Analytics, TikTok Pixel, and Facebook Pixel
 * @param {string} eventType - Event type (ViewContent, AddToCart, etc.)
 * @param {Object} [options={}] - Event options
 * @param {Array} [options.contents] - Array of content objects for TikTok and Facebook
 * @param {number} [options.value] - Event value
 * @param {string} [options.currency="USD"] - Currency code (ISO 4217)
 * @param {string} [options.search_string] - Search keywords (for Search event)
 * @param {Object} [options.userData] - User identification data for TikTok and Facebook
 * @param {string} [options.eventId] - Event ID shared with Facebook Pixel/server-side tracking
 * @param {string} [options.category] - GA category (for backward compatibility)
 * @param {string} [options.action] - GA action (for backward compatibility)
 * @param {string} [options.label] - GA label (for backward compatibility)
 */
export function trackEvent(eventType, options = {}) {
  const {
    contents = [],
    value,
    currency = "USD",
    search_string,
    userData,
    eventId,
    // Backward compatibility for Google Analytics
    category,
    action,
    label
  } = options;

  // Handle user identification for TikTok and Facebook
  if (userData && Object.keys(userData).length > 0) {
    identifyTikTokUser(userData);
    identifyFacebookUser(userData);
  }

  // TikTok event mapping
  const tikTokEvents = {
    'ViewContent': 'ViewContent',
    'AddToWishlist': 'AddToWishlist',
    'Search': 'Search',
    'AddPaymentInfo': 'AddPaymentInfo',
    'AddToCart': 'AddToCart',
    'InitiateCheckout': 'InitiateCheckout',
    'PlaceAnOrder': 'PlaceAnOrder',
    'CompleteRegistration': 'CompleteRegistration',
    'Purchase': 'Purchase'
  };

  // Facebook event mapping
  const facebookEvents = {
    'ViewContent': 'ViewContent',
    'AddToWishlist': 'AddToWishlist',
    'Search': 'Search',
    'AddPaymentInfo': 'AddPaymentInfo',
    'AddToCart': 'AddToCart',
    'InitiateCheckout': 'InitiateCheckout',
    'CompleteRegistration': 'CompleteRegistration',
    'Purchase': 'Purchase',
    'Lead': 'Lead',
    'Contact': 'Contact',
    'CustomizeProduct': 'CustomizeProduct',
    'Donate': 'Donate',
    'FindLocation': 'FindLocation',
    'Schedule': 'Schedule',
    'StartTrial': 'StartTrial',
    'SubmitApplication': 'SubmitApplication',
    'Subscribe': 'Subscribe'
  };

  const resolvedFacebookEventId = facebookEvents[eventType]
    ? (eventId || generateEventId())
    : eventId;

  if (tikTokEvents[eventType]) {
    // Build TikTok parameters
    const tikTokParams = {
      contents: contents.map(content => ({
        content_id: content.content_id || content.id,
        content_type: content.content_type || 'product',
        content_name: content.content_name || content.name
      })),
      currency: currency,
      // TikTok requires value to be a number (integer or decimal) without currency symbols, letters, special characters, or commas
      // Value is required, so we always include it after normalization
      value: normalizeValue(value)
    };

    // Add search_string for Search event
    if (eventType === 'Search' && search_string) {
      tikTokParams.search_string = search_string;
    }

    trackTikTokEvent(eventType, tikTokParams);
  }

  if (facebookEvents[eventType]) {
    // Build Facebook parameters
    const facebookParams = {
      content_ids: contents.map(content => content.content_id || content.id),
      content_name: contents.length > 0 ? (contents[0].content_name || contents[0].name) : undefined,
      content_type: contents.length > 0 ? (contents[0].content_type || 'product') : 'product',
      currency: currency,
      // Normalize value to ensure it's a valid number
      value: normalizeValue(value)
    };

    // Add search_string for Search event
    if (eventType === 'Search' && search_string) {
      facebookParams.search_string = search_string;
    }

    // Remove undefined values
    Object.keys(facebookParams).forEach(key => {
      if (facebookParams[key] === undefined) {
        delete facebookParams[key];
      }
    });

    trackFacebookEvent(eventType, facebookParams, {
      eventId: resolvedFacebookEventId
    });
  }

  // Backward compatibility with Google Analytics
  if (category && action) {
    trackGoogleAnalytics(category, action, label, value);
  }

  return {
    eventId: resolvedFacebookEventId
  };
}
/*

// Universal events (work with TikTok Pixel, Facebook Pixel, and Google Analytics)
trackEvent('ViewContent', {
  contents: [{ content_id: '123', content_name: 'Product Name' }],
  value: 99.99,
  currency: 'USD'
});

// AddToCart with user identification (supports both TikTok and Facebook)
trackEvent('AddToCart', {
  contents: [{ content_id: '123', content_name: 'Product Name' }],
  value: 99.99,
  currency: 'USD',
  userData: {
    email: 'hashed_email@example.com', // SHA-256 hashed
    phone_number: 'hashed_phone@example.com', // SHA-256 hashed (optional)
    first_name: 'hashed_first_name', // SHA-256 hashed (Facebook only)
    last_name: 'hashed_last_name' // SHA-256 hashed (Facebook only)
  }
});

// Search event
trackEvent('Search', {
  contents: [{ content_id: 'search_results', content_name: 'Search Results' }],
  value: 0,
  currency: 'USD',
  search_string: 'red shoes'
});

// Purchase event
trackEvent('Purchase', {
  contents: [
    { content_id: '123', content_name: 'Product A', content_type: 'product' },
    { content_id: '456', content_name: 'Product B', content_type: 'product' }
  ],
  value: 199.99,
  currency: 'USD'
});

// Lead/Contact event
trackEvent('Lead', {
  contents: [{ content_id: 'contact_form', content_name: 'Contact Form' }],
  value: 0,
  currency: 'USD'
});

// Backward compatibility with Google Analytics
trackEvent('ecommerce', {
  category: 'ecommerce',
  action: 'purchase',
  label: 'Product Purchase',
  value: 199.99
});
*/
